import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Heart,
  MessageCircle,
  User,
  Calendar,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Tag,
  Edit,
  Loader2,
  Maximize2,
  X,
  Send,
  Trash2,
  Pencil,
  Check,
  Coffee,
  DollarSign,
  Gift,
  ExternalLink,
  PanelRight,
  Reply,
  Smile
} from 'lucide-react';
import { toast } from 'sonner';

const ComicViewStreamlined = () => {
  const { id } = useParams();
  const { user, token, API } = useContext(AuthContext);
  const [comic, setComic] = useState(null);
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(0);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  // Tip/Support state
  const [showTipDropdown, setShowTipDropdown] = useState(false);
  const [supportLinks, setSupportLinks] = useState(null);
  const [tipPackages, setTipPackages] = useState({});
  const [paymentProviders, setPaymentProviders] = useState({});
  const [selectedProvider, setSelectedProvider] = useState('razorpay');
  const [processingTip, setProcessingTip] = useState(false);
  const [showCustomTip, setShowCustomTip] = useState(false);
  const [customTipAmount, setCustomTipAmount] = useState('');
  const [tipMessage, setTipMessage] = useState('');
  const [platformFee, setPlatformFee] = useState(10);
  const [creatorComics, setCreatorComics] = useState([]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isSidebarTab, setIsSidebarTab] = useState('chat');
  const [showControls, setShowControls] = useState(true);
  const [controlsTimeout, setControlsTimeout] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(null);
  const [messageReactions, setMessageReactions] = useState({});

  // Handle auto-hide controls in fullscreen
  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeout) clearTimeout(controlsTimeout);
    const timeout = setTimeout(() => {
      if (isFullscreen) setShowControls(false);
    }, 3000);
    setControlsTimeout(timeout);
  };

  useEffect(() => {
    return () => {
      if (controlsTimeout) clearTimeout(controlsTimeout);
    };
  }, [controlsTimeout]);

  useEffect(() => {
    fetchComic();
    fetchComments();
  }, [id]);

  const fetchComic = async () => {
    try {
      const response = await axios.get(`${API}/streamlined-comics/${id}`);
      setComic(response.data);
      setLikesCount(response.data.likes_count || 0);

      // Fetch creator's support links and other comics
      if (response.data.author_id) {
        fetchSupportLinks(response.data.author_id);
        fetchCreatorComics(response.data.author_id, response.data.id);
      }

      // Check if user has liked this comic
      if (token) {
        try {
          const likeResponse = await axios.get(`${API}/streamlined-comics/${id}/like-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setIsLiked(likeResponse.data.is_liked);
        } catch (e) {
          // Endpoint might not exist yet
        }
      }
    } catch (error) {
      console.error('Error fetching comic:', error);
      toast.error('Failed to load comic');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const response = await axios.get(`${API}/streamlined-comics/${id}/comments`);
      setComments(response.data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('Please login to like comics');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/streamlined-comics/${id}/like`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsLiked(response.data.is_liked);
      setLikesCount(response.data.likes_count);
      toast.success(response.data.is_liked ? 'Comic liked!' : 'Like removed');
    } catch (error) {
      console.error('Error liking comic:', error);
      toast.error('Failed to like comic');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to comment');
      return;
    }
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await axios.post(
        `${API}/streamlined-comics/${id}/comments`,
        { content: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment posted!');
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Edit comment state
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editCommentText, setEditCommentText] = useState('');

  const handleEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditCommentText(comment.content);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditCommentText('');
  };

  const handleSaveEdit = async (commentId) => {
    if (!editCommentText.trim()) return;

    try {
      const response = await axios.put(
        `${API}/comments/${commentId}`,
        { content: editCommentText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments(comments.map(c =>
        c.id === commentId ? { ...c, content: editCommentText } : c
      ));
      setEditingCommentId(null);
      setEditCommentText('');
      toast.success('Comment updated!');
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Failed to update comment');
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  // Tip/Support functions
  const fetchSupportLinks = async (authorId) => {
    try {
      const response = await axios.get(`${API}/user/${authorId}/support-links`);
      setSupportLinks(response.data.support_links || {});
      setTipPackages(response.data.tip_packages || {});

      // Fetch payment providers and packages
      const packagesResponse = await axios.get(`${API}/tips/packages`);
      if (packagesResponse.data) {
        setTipPackages(packagesResponse.data.packages || {});
        setPaymentProviders(packagesResponse.data.providers || {});
        setPlatformFee(packagesResponse.data.platform_fee_percent || 10);

        // Set default provider (razorpay is default)
        const providers = packagesResponse.data.providers || {};
        const defaultProvider = Object.keys(providers).find(k => providers[k].default) || 'razorpay';
        setSelectedProvider(defaultProvider);
      }
    } catch (error) {
      console.error('Error fetching support links:', error);
    }
  };

  // Fetch other comics from the same creator
  const fetchCreatorComics = async (authorId, currentComicId) => {
    try {
      const response = await axios.get(`${API}/streamlined-comics`, {
        params: { author_id: authorId, limit: 6 }
      });
      // Filter out the current comic and take up to 5 others
      const otherComics = (response.data || [])
        .filter(c => c.id !== currentComicId)
        .slice(0, 5);
      setCreatorComics(otherComics);
    } catch (error) {
      console.error('Error fetching creator comics:', error);
    }
  };

  const handleTip = async (packageId) => {
    if (!comic) return;

    // Don't allow tipping yourself
    if (user && user.id === comic.author_id) {
      toast.info("You can't tip yourself!");
      return;
    }

    setProcessingTip(true);

    try {
      const currency = selectedProvider === 'razorpay' ? 'INR' : 'USD';

      const tipData = {
        creator_id: comic.author_id,
        package_id: packageId,
        message: tipMessage || null,
        payment_provider: selectedProvider,
        currency: currency
      };

      if (packageId === 'custom') {
        const amount = parseFloat(customTipAmount);
        if (isNaN(amount) || amount < 1) {
          toast.error('Please enter a valid amount (minimum 1)');
          setProcessingTip(false);
          return;
        }
        if (selectedProvider === 'paypal' && amount < 15) {
          toast.error('Minimum tip via PayPal is $15');
          setProcessingTip(false);
          return;
        }
        tipData.custom_amount = amount;
      }

      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/tips/create-session`,
        tipData,
        { headers }
      );

      // Handle response based on provider
      if (selectedProvider === 'stripe' && response.data.checkout_url) {
        toast.info(`${comic.author_name} receives ${currency === 'INR' ? '₹' : '$'}${response.data.creator_receives} (${platformFee}% platform fee)`);
        window.location.href = response.data.checkout_url;

      } else if (selectedProvider === 'razorpay' && response.data.order_id) {
        // Load Razorpay script and open payment modal
        const options = {
          key: response.data.razorpay_key,
          amount: response.data.amount_paise,
          currency: response.data.currency,
          name: 'ComicMenia',
          description: `Tip for ${response.data.creator_name}`,
          order_id: response.data.order_id,
          handler: async function (paymentResponse) {
            // Verify payment
            try {
              await axios.post(`${API}/razorpay/verify`, {
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature
              }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });

              toast.success(`Thank you! ${comic.author_name} received ₹${response.data.creator_receives}`);
              setShowTipDropdown(false);
            } catch (err) {
              toast.error('Payment verification failed');
            }
          },
          prefill: response.data.prefill,
          theme: { color: '#8B5CF6' }
        };

        // Check if Razorpay script is loaded
        if (window.Razorpay) {
          const rzp = new window.Razorpay(options);
          rzp.open();
        } else {
          toast.error('Payment system loading... Please try again.');
        }

      } else if (selectedProvider === 'paypal' && response.data.approval_url) {
        toast.info(`${comic.author_name} receives $${response.data.creator_receives} (${platformFee}% platform fee)`);
        window.location.href = response.data.approval_url;

      } else if (selectedProvider === 'cashfree' && response.data.payment_session_id) {
        // Initialize Cashfree
        const cashfree = new window.Cashfree({
          mode: response.data.cashfree_env === 'PROD' ? 'production' : 'sandbox'
        });

        const checkoutOptions = {
          paymentSessionId: response.data.payment_session_id,
          redirectTarget: "_modal" // or "_self"
        };

        cashfree.checkout(checkoutOptions)
          .then(function (result) {
            if (result.error) {
              toast.error('Payment failed/cancelled');
              console.error(result.error);
            }
            if (result.redirect) {
              console.log("Redirection");
            }
            if (result.paymentDetails) {
              console.log("Payment OK");
              // Verify on our backend
              axios.post(`${API}/cashfree/verify`, {
                order_id: response.data.order_id
              }, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
                .then(res => {
                  if (res.data.status === 'PAID') {
                    toast.success(`Thank you! ${comic.author_name} received ₹${response.data.creator_receives}`);
                    setShowTipDropdown(false);
                  } else {
                    toast.warning('Payment verification pending. Please check back later.');
                  }
                })
                .catch(err => toast.error('Verification failed'));
            }
          });
      }

    } catch (error) {
      console.error('Error creating tip:', error);
      toast.error(error.response?.data?.detail || 'Failed to process tip');
    } finally {
      setProcessingTip(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const currentEpisode = comic?.episodes[currentEpisodeIndex];
  const currentSlide = currentEpisode?.slides[currentSlideIndex];

  const nextSlide = () => {
    if (currentEpisode && currentSlideIndex < currentEpisode.slides.length - 1) {
      setCurrentSlideIndex(prev => prev + 1);
    } else if (currentEpisodeIndex < comic.episodes.length - 1) {
      setCurrentEpisodeIndex(prev => prev + 1);
      setCurrentSlideIndex(0);
    }
  };

  const prevSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(prev => prev - 1);
    } else if (currentEpisodeIndex > 0) {
      setCurrentEpisodeIndex(prev => prev - 1);
      const prevEpisode = comic.episodes[currentEpisodeIndex - 1];
      setCurrentSlideIndex(prevEpisode.slides.length - 1);
    }
  };

  const switchEpisode = (episodeIndex) => {
    setCurrentEpisodeIndex(episodeIndex);
    setCurrentSlideIndex(0);
  };

  const toggleFullscreen = () => {
    console.log('Toggling fullscreen, current state:', isFullscreen);
    setIsFullscreen(prev => !prev);
  };

  // Keyboard navigation for fullscreen
  useEffect(() => {
    if (!isFullscreen) return;

    const handleKeyPress = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isFullscreen]);

  if (loading) {
    return (
      <div className="min-h-screen hero-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-purple-400" />
          <p className="text-gray-400">Loading comic...</p>
        </div>
      </div>
    );
  }

  if (!comic) {
    return (
      <div className="min-h-screen hero-background flex items-center justify-center">
        <Card className="glass-card max-w-md w-full neon-border">
          <CardContent className="p-6 text-center">
            <BookOpen className="w-16 h-16 text-purple-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Comic not found</h2>
            <p className="text-gray-400 mb-6">The comic you&apos;re looking for doesn&apos;t exist or has been removed.</p>
            <Link to="/" className="btn-primary inline-flex items-center px-4 py-2 rounded-lg">
              Go Home
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isOwner = user && user.id === comic.author_id;

  // Fullscreen scroll-based reader (Cinema Mode)
  if (isFullscreen) {
    return (
      <div
        className="fixed inset-0 z-50 bg-black overflow-y-auto custom-scrollbar-hide"
        onMouseMove={handleMouseMove}
        onClick={() => setShowControls(prev => !prev)}
      >
        {/* Ambient Background Glow (Subtle) */}
        <div className="fixed inset-0 pointer-events-none opacity-20 transition-opacity duration-1000">
          {/* We could use the current slide as a blur here, but let's keep it deep black for true cinema feeling */}
          <div className="absolute inset-0 bg-gradient-to-b from-black via-transparent to-black" />
        </div>

        {/* Floating Header - Auto Hides */}
        <div className={`fixed top-0 left-0 right-0 z-[60] p-6 bg-gradient-to-b from-black/90 to-transparent transition-all duration-500 transform ${showControls ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}>
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <h1 className="text-white/90 font-bold text-xl tracking-tight shadow-black drop-shadow-md">{comic.title}</h1>
              <span className="text-gray-400 text-sm border-l border-gray-700 pl-4">{currentEpisode?.title}</span>
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleFullscreen();
              }}
              className="group flex items-center space-x-2 bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full backdrop-blur-md transition-all border border-white/5"
            >
              <span className="text-sm text-gray-300 group-hover:text-white">Exit Cinema</span>
              <X className="w-5 h-5 text-gray-300 group-hover:text-white" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="min-h-screen py-20 flex flex-col items-center">
          {comic.episodes.map((episode, epIndex) => (
            <div key={epIndex} className="w-full max-w-5xl mx-auto mb-32 last:mb-0">

              {/* Slides - Vertical Stack with optimized spacing */}
              <div className="flex flex-col items-center">
                {episode.slides.map((slide, slideIndex) => (
                  <div key={slideIndex} className="relative w-full group mb-1">
                    <img
                      src={slide}
                      alt={`${episode.title} - Slide ${slideIndex + 1}`}
                      className="w-full h-auto shadow-2xl"
                      style={{ maxWidth: '1000px', margin: '0 auto', display: 'block' }}
                    />

                    {/* Page Number Overlay (Only distinct when controls shown) */}
                    <div className={`absolute bottom-4 right-4 bg-black/50 backdrop-blur-sm px-3 py-1 rounded-full border border-white/5 transition-opacity duration-500 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
                      <p className="text-white/70 text-xs font-mono">
                        {slideIndex + 1} / {episode.slides.length}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Floating Footer / Progress Hint */}
        <div className={`fixed bottom-0 left-0 right-0 z-[60] p-8 bg-gradient-to-t from-black/90 to-transparent transition-all duration-500 transform ${showControls ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`}>
          <div className="max-w-md mx-auto text-center">
            <p className="text-white/40 text-sm font-medium tracking-widest uppercase mb-2">Cinema Mode</p>
            <div className="w-full h-1 bg-gray-800 rounded-full overflow-hidden">
              {/* A simple progress bar could go here if we tracked scroll percentage, keeping it simple for now */}
              <div className="w-1/2 h-full bg-purple-900/50 mx-auto rounded-full blur-sm" />
            </div>
            <p className="text-xs text-gray-500 mt-2">Scroll to read • Press ESC to exit</p>
          </div>
        </div>
      </div>
    );
  }

  // Normal view with dark theme
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col md:flex-row overflow-hidden relative">
      {/* Main Viewer Area */}
      <div className={`relative flex-1 flex flex-col h-screen transition-all duration-300 ${showTipDropdown ? 'z-40' : 'z-0'}`}>

        {/* Top Navbar Overlay */}
        <div className="absolute top-0 left-0 right-0 z-20 p-4 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent">
          <Link to="/" className="flex items-center text-gray-300 hover:text-white transition-colors bg-black/40 hover:bg-black/60 px-4 py-2 rounded-full backdrop-blur-md border border-white/10">
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">Home</span>
          </Link>

          <div className="flex items-center space-x-2">
            {/* Episode Title Badge */}
            <div className="px-4 py-1.5 rounded-full bg-purple-900/40 border border-purple-500/30 backdrop-blur-md text-purple-200 text-sm font-medium shadow-[0_0_15px_rgba(168,85,247,0.2)]">
              {currentEpisode?.title}
            </div>

            {/* Like Button */}
            <button
              onClick={handleLike}
              className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full backdrop-blur-md border transition-all ${isLiked
                ? 'bg-red-500/20 border-red-500/50 text-red-400 shadow-[0_0_15px_rgba(239,68,68,0.3)]'
                : 'bg-black/40 border-white/10 text-gray-300 hover:bg-white/10'
                }`}
            >
              <Heart className={`w-4 h-4 ${isLiked ? 'fill-red-400' : ''}`} />
              <span className="text-sm">{likesCount}</span>
            </button>
          </div>

          {/* Right Drawer Toggle (Mobile Only) / Extra Actions */}
          <div className="flex items-center space-x-2">
            {isOwner && (
              <Link to={`/edit-comic/${comic.id}`} className="p-2 rounded-full bg-black/40 border border-white/10 text-gray-300 hover:text-white hover:bg-white/10 transition-all backdrop-blur-md">
                <Edit className="w-4 h-4" />
              </Link>
            )}
            {/* Mobile Sidebar Toggle would go here if needed, but we'll stick to desktop layout for now */}
            {/* Sidebar Toggle */}
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className={`p-2 rounded-full backdrop-blur-md border border-white/10 transition-all ${isSidebarOpen
                ? 'bg-purple-600/20 text-purple-400 border-purple-500/30'
                : 'bg-black/40 text-gray-300 hover:text-white'
                }`}
              title={isSidebarOpen ? "Close Sidebar" : "Open Sidebar"}
            >
              <PanelRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Main Image Area */}
        <div className="flex-1 relative flex items-center justify-center bg-[#050505] overflow-hidden group">
          {/* Dynamic Blurred Background */}
          <div
            className="absolute inset-0 opacity-20 blur-3xl transition-all duration-700"
            style={{
              backgroundImage: `url(${currentSlide})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-[#0a0a0a]/50" />

          {/* The Slide */}
          <div className="relative z-10 w-full h-full flex items-center justify-center p-4 md:p-8">
            <img
              src={currentSlide}
              alt={`${currentEpisode?.title} - Slide ${currentSlideIndex + 1}`}
              className="max-h-full max-w-full object-contain drop-shadow-2xl rounded-sm transition-transform duration-300"
            />
          </div>

          {/* Navigation Controls (Hover visible) */}
          <button
            onClick={prevSlide}
            disabled={currentEpisodeIndex === 0 && currentSlideIndex === 0}
            className="absolute left-4 z-20 p-4 rounded-full bg-black/30 text-white/50 hover:bg-purple-600 hover:text-white hover:scale-110 transition-all backdrop-blur-sm border border-white/5 disabled:opacity-0 disabled:cursor-not-allowed group-hover:opacity-100 opacity-0 transform -translate-x-4 group-hover:translate-x-0 duration-300"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          <button
            onClick={nextSlide}
            disabled={
              currentEpisodeIndex === comic.episodes.length - 1 &&
              currentSlideIndex === currentEpisode?.slides.length - 1
            }
            className="absolute right-4 z-20 p-4 rounded-full bg-black/30 text-white/50 hover:bg-purple-600 hover:text-white hover:scale-110 transition-all backdrop-blur-sm border border-white/5 disabled:opacity-0 disabled:cursor-not-allowed group-hover:opacity-100 opacity-0 transform translate-x-4 group-hover:translate-x-0 duration-300"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          {/* Fullscreen Trigger */}
          <button
            onClick={toggleFullscreen}
            className="absolute bottom-6 right-6 z-20 flex items-center space-x-2 px-4 py-2 rounded-full bg-purple-600 text-white shadow-[0_0_20px_rgba(168,85,247,0.4)] hover:shadow-[0_0_30px_rgba(168,85,247,0.6)] hover:bg-purple-500 hover:scale-105 transition-all text-sm font-medium"
          >
            <Maximize2 className="w-4 h-4 mr-2" />
            <span>Cinema Mode</span>
          </button>

          {/* Page Indicator */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20 px-4 py-1.5 rounded-full bg-black/50 border border-white/10 backdrop-blur-md text-white/80 text-xs font-medium tracking-wider">
            PAGE {currentSlideIndex + 1} / {currentEpisode?.slides.length}
          </div>
        </div>
      </div>

      {/* Right Sidebar Drawer */}
      {isSidebarOpen && (
        <div className="w-full md:w-[400px] bg-[#111111] border-l border-white/5 flex flex-col h-[50vh] md:h-screen relative z-30 shadow-2xl animate-in slide-in-from-right duration-300">
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Tabs Header */}
            <div className="flex items-center p-2 bg-[#0f0f0f] border-b border-white/5">
              {[
                { id: 'chat', icon: MessageCircle, label: 'Chat' },
                { id: 'episodes', icon: BookOpen, label: 'Episodes' },
                { id: 'info', icon: Calendar, label: 'Info' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setIsSidebarTab(tab.id)}
                  className={`flex-1 flex items-center justify-center py-3 rounded-lg text-sm font-medium transition-all ${isSidebarTab === tab.id
                    ? 'bg-purple-600/10 text-purple-400 ring-1 ring-purple-500/20'
                    : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                    }`}
                >
                  <tab.icon className={`w-4 h-4 mr-2 ${isSidebarTab === tab.id ? 'animate-pulse' : ''}`} />
                  {tab.label}
                  {tab.id === 'chat' && (
                    <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-gray-800 text-[10px] text-gray-400">
                      {comments.length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Tab Content Area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar bg-[#111111] relative">

              {/* CHAT TAB */}
              {isSidebarTab === 'chat' && (
                <div className="flex flex-col h-full bg-[#111111]">
                  {/* Comments List */}
                  <div className="flex-1 p-4 space-y-6 overflow-y-auto">
                    {loadingComments ? (
                      <div className="flex items-center justify-center py-10">
                        <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                      </div>
                    ) : comments.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center p-6 opacity-60">
                        <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center mb-4">
                          <MessageCircle className="w-8 h-8 text-gray-600" />
                        </div>
                        <p className="text-gray-400 text-sm">No comments yet.<br />Be the first to start the conversation!</p>
                      </div>
                    ) : (
                      comments.map((comment, idx) => {
                        const isMe = user && user.id === comment.author_id;
                        const reactions = messageReactions[comment.id] || {};
                        return (
                          <div key={comment.id} className={`flex gap-3 ${isMe ? 'flex-row-reverse' : ''} group animate-in slide-in-from-bottom-2 duration-300 fade-in`} style={{ animationDelay: `${idx * 50}ms` }}>
                            {/* Avatar */}
                            <div className="flex-shrink-0">
                              {comment.author_avatar ? (
                                <img
                                  src={comment.author_avatar}
                                  alt={comment.author_name}
                                  className={`w-8 h-8 rounded-full object-cover ring-2 ring-black ${isMe ? 'ring-purple-500/50' : ''}`}
                                />
                              ) : (
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ring-2 ring-black ${isMe ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300'}`}>
                                  {comment.author_name ? comment.author_name.charAt(0).toUpperCase() : '?'}
                                </div>
                              )}
                            </div>

                            {/* Bubble */}
                            <div className={`flex flex-col max-w-[85%] ${isMe ? 'items-end' : 'items-start'}`}>
                              <div className="flex items-baseline space-x-2 mb-1 px-1">
                                <span className="text-[11px] font-bold text-gray-400">{isMe ? 'You' : comment.author_name}</span>
                                <span className="text-[10px] text-gray-600">{new Date(comment.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                              </div>

                              <div className={`p-3 rounded-2xl text-sm leading-relaxed relative group/bubble ${isMe
                                ? 'bg-purple-600/20 text-purple-100 rounded-tr-sm border border-purple-500/20'
                                : 'bg-gray-800 text-gray-200 rounded-tl-sm border border-gray-700'
                                }`}>
                                {editingCommentId === comment.id ? (
                                  <div className="min-w-[200px]">
                                    <Textarea
                                      value={editCommentText}
                                      onChange={(e) => setEditCommentText(e.target.value)}
                                      className="bg-black/20 border-white/10 text-xs mb-2 h-16 resize-none focus:ring-0"
                                      autoFocus
                                    />
                                    <div className="flex justify-end gap-2">
                                      <button onClick={handleCancelEdit} className="text-xs text-gray-400 hover:text-white">Cancel</button>
                                      <button onClick={() => handleSaveEdit(comment.id)} className="text-xs text-purple-400 hover:text-purple-300 font-semibold">Save</button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {comment.reply_to && (
                                      <div className="text-[10px] text-purple-400/70 mb-1 italic border-l-2 border-purple-500/30 pl-2">↩ replying to @{comment.reply_to}</div>
                                    )}
                                    {comment.content}
                                  </>
                                )}

                                {/* Action Buttons (Hover) */}
                                {!editingCommentId && (
                                  <div className={`absolute ${isMe ? '-left-20' : '-right-20'} top-0 opacity-0 group-hover/bubble:opacity-100 transition-all flex gap-0.5 bg-black/70 rounded-lg backdrop-blur-sm border border-white/10 shadow-lg`}>
                                    {/* Reply */}
                                    <button
                                      onClick={() => {
                                        setReplyingTo(comment.author_name);
                                        setNewComment(`@${comment.author_name} `);
                                      }}
                                      className="p-1.5 hover:text-blue-400 text-gray-500 transition-colors" title="Reply"
                                    >
                                      <Reply className="w-3 h-3" />
                                    </button>
                                    {/* Reaction */}
                                    <button
                                      onClick={() => setShowEmojiPicker(showEmojiPicker === comment.id ? null : comment.id)}
                                      className="p-1.5 hover:text-yellow-400 text-gray-500 transition-colors" title="React"
                                    >
                                      <Smile className="w-3 h-3" />
                                    </button>
                                    {/* Edit (own only) */}
                                    {isMe && (
                                      <button onClick={() => handleEditComment(comment)} className="p-1.5 hover:text-purple-400 text-gray-500 transition-colors" title="Edit">
                                        <Pencil className="w-3 h-3" />
                                      </button>
                                    )}
                                    {/* Delete (own only) */}
                                    {isMe && (
                                      <button onClick={() => handleDeleteComment(comment.id)} className="p-1.5 hover:text-red-400 text-gray-500 transition-colors" title="Delete">
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                )}

                                {/* Emoji Picker Dropdown */}
                                {showEmojiPicker === comment.id && (
                                  <div className={`absolute ${isMe ? 'left-0' : 'right-0'} -bottom-9 flex gap-1 bg-black/80 backdrop-blur-md rounded-full px-2 py-1 border border-white/10 shadow-xl z-50`}>
                                    {['❤️', '😂', '🔥', '👏', '😮', '💯'].map(emoji => (
                                      <button
                                        key={emoji}
                                        onClick={() => {
                                          setMessageReactions(prev => {
                                            const existing = prev[comment.id] || {};
                                            const count = existing[emoji] || 0;
                                            return { ...prev, [comment.id]: { ...existing, [emoji]: count + 1 } };
                                          });
                                          setShowEmojiPicker(null);
                                        }}
                                        className="text-sm hover:scale-125 transition-transform p-0.5"
                                      >
                                        {emoji}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Reaction Pills */}
                              {Object.keys(reactions).length > 0 && (
                                <div className="flex gap-1 mt-1 px-1">
                                  {Object.entries(reactions).map(([emoji, count]) => (
                                    <span key={emoji} className="flex items-center gap-0.5 bg-gray-800/80 border border-gray-700 rounded-full px-1.5 py-0.5 text-[10px] cursor-pointer hover:border-purple-500/50 transition-colors">
                                      {emoji} <span className="text-gray-400">{count}</span>
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })
                    )}
                    <div className="h-4" /> {/* Spacer */}
                  </div>

                  {/* Chat Input Area */}
                  <div className="p-4 bg-[#0a0a0a] border-t border-white/5">
                    {user ? (
                      <form onSubmit={handleSubmitComment} className="relative">
                        {replyingTo && (
                          <div className="flex items-center justify-between mb-2 px-2 py-1.5 bg-blue-900/20 border border-blue-500/20 rounded-lg">
                            <span className="text-xs text-blue-400"><Reply className="w-3 h-3 inline mr-1" />Replying to <span className="font-semibold">@{replyingTo}</span></span>
                            <button onClick={() => { setReplyingTo(null); setNewComment(''); }} className="text-gray-500 hover:text-white"><X className="w-3 h-3" /></button>
                          </div>
                        )}
                        {user.email && (
                          <div className="flex items-center space-x-2 mb-2 text-xs text-gray-500 pl-1">
                            {user.avatar ? (
                              <img src={user.avatar} alt="You" className="w-4 h-4 rounded-full object-cover ring-1 ring-purple-500/30" />
                            ) : (
                              <div className="w-4 h-4 rounded-full bg-purple-900/50 flex items-center justify-center text-[9px] text-purple-400 ring-1 ring-purple-500/30">
                                {user.username?.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <span>Chatting as <span className="text-gray-300">{user.display_name || user.username}</span></span>
                          </div>
                        )}
                        <div className="relative group">
                          <Textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Type a message..."
                            className="w-full bg-[#151515] border-transparent focus:border-purple-500/50 focus:ring-1 focus:ring-purple-500/20 text-gray-200 placeholder:text-gray-600 pl-4 pr-12 py-3 rounded-xl resize-none min-h-[50px] max-h-[120px] transition-all"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmitComment(e);
                              }
                            }}
                          />
                          <button
                            type="submit"
                            disabled={!newComment.trim() || submittingComment}
                            className="absolute right-2 bottom-2 p-2 rounded-lg bg-purple-600 text-white hover:bg-purple-500 disabled:opacity-50 disabled:bg-transparent disabled:text-gray-600 transition-all shadow-lg shadow-purple-900/20"
                          >
                            {submittingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                          </button>
                        </div>
                        <p className="text-[10px] text-gray-600 mt-2 text-center">Press Enter to send</p>
                      </form>
                    ) : (
                      <div className="text-center p-4 rounded-xl bg-gradient-to-br from-gray-900 to-gray-800 border border-white/5">
                        <p className="text-sm text-gray-400 mb-3">Join the conversation</p>
                        <Link
                          to="/login"
                          className="block w-full py-2.5 rounded-lg bg-white text-black font-semibold text-sm hover:bg-gray-200 transition-colors shadow-lg"
                        >
                          Sign In to Chat
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* EPISODES TAB */}
              {isSidebarTab === 'episodes' && (
                <div className="p-4 space-y-3">
                  {comic.episodes.map((episode, index) => (
                    <button
                      key={index}
                      onClick={() => switchEpisode(index)}
                      className={`w-full group flex items-start p-3 rounded-xl transition-all border ${currentEpisodeIndex === index
                        ? 'bg-purple-600/10 border-purple-500/30 ring-1 ring-purple-500/20'
                        : 'bg-gray-900/40 border-white/5 hover:bg-gray-800 hover:border-white/10'
                        }`}
                    >
                      <div className={`mt-1 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-3 transition-colors ${currentEpisodeIndex === index ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-500 group-hover:bg-gray-700'
                        }`}>
                        {index + 1}
                      </div>
                      <div className="text-left flex-1">
                        <h4 className={`text-sm font-medium ${currentEpisodeIndex === index ? 'text-white' : 'text-gray-300 group-hover:text-white'}`}>
                          {episode.title}
                        </h4>
                        <span className="text-xs text-gray-500 mt-0.5 block">{episode.slides.length} pages</span>
                      </div>
                      {currentEpisodeIndex === index && (
                        <div className="self-center">
                          <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* INFO TAB */}
              {isSidebarTab === 'info' && (
                <div className="p-6 space-y-8">
                  {/* Tip Banner */}
                  {comic.tips_enabled !== false && comic.author_id !== user?.id && (
                    <div className="rounded-2xl bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-orange-500/20 p-5 text-center relative overflow-hidden group hover:border-orange-500/40 transition-all">
                      <div className="absolute inset-0 bg-orange-500/5 blur-xl group-hover:bg-orange-500/10 transition-colors" />
                      <Gift className="w-8 h-8 text-orange-400 mx-auto mb-2 relative z-10" />
                      <h3 className="text-white font-semibold relative z-10">Support the Creator</h3>
                      <p className="text-xs text-orange-200/70 mb-4 mt-1 relative z-10">Help {comic.author_name} create more amazing content!</p>
                      <button
                        onClick={() => setShowTipDropdown(!showTipDropdown)}
                        className="relative z-10 w-full py-2 bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-bold rounded-lg text-sm shadow-lg shadow-orange-900/20 hover:shadow-orange-900/40 hover:scale-105 transition-all"
                      >
                        Appreciate Now
                      </button>

                      {/* Tip Modal - Shows when showTipDropdown is true */}
                      {showTipDropdown && (
                        <div className="mt-4 p-4 bg-black/60 backdrop-blur-md rounded-xl border border-white/10 relative z-20">
                          <div className="flex justify-between items-center mb-4">
                            <h4 className="text-white font-semibold text-sm">Choose a tip amount</h4>
                            <button
                              onClick={() => setShowTipDropdown(false)}
                              className="text-gray-400 hover:text-white text-xl"
                            >
                              ×
                            </button>
                          </div>

                          {/* Payment Provider Selection */}
                          <div className="mb-4">
                            <label className="text-xs text-gray-400 mb-2 block">Payment Method</label>
                            <div className="grid grid-cols-2 gap-2">
                              {Object.entries(paymentProviders).map(([key, provider]) => (
                                <button
                                  key={key}
                                  onClick={() => setSelectedProvider(key)}
                                  className={`py-2 px-3 rounded-lg text-xs font-medium transition-all ${selectedProvider === key
                                    ? 'bg-purple-600 text-white border-purple-500'
                                    : 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700'
                                    } border`}
                                >
                                  {provider.name}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* Tip Packages */}
                          <div className="grid grid-cols-2 gap-2 mb-4">
                            {Object.entries(tipPackages).map(([key, pkg]) => {
                              const isPayPal = selectedProvider === 'paypal';
                              const isBelowLimit = isPayPal && pkg.amount_usd < 15;

                              return (
                                <button
                                  key={key}
                                  onClick={() => {
                                    if (isBelowLimit) {
                                      toast.error("Minimum tip for PayPal is $15");
                                      return;
                                    }
                                    handleTip(key);
                                  }}
                                  disabled={processingTip}
                                  className={`p-3 rounded-lg border transition-all text-left group ${isBelowLimit
                                    ? 'bg-gray-900 border-gray-800 opacity-40'
                                    : 'bg-gray-800 hover:bg-gray-700 border-gray-700 hover:border-purple-500/50'
                                    }`}
                                >
                                  <span className="text-lg block mb-1">{pkg.label?.split(' ')[0] || '💜'}</span>
                                  <span className="text-white text-sm font-medium block">
                                    ₹{pkg.amount_inr}
                                  </span>
                                  <span className="text-[10px] text-gray-500 block mt-0.5">
                                    Approx ${pkg.amount_usd} USD
                                  </span>
                                  <span className="text-xs text-gray-500">{pkg.label?.split(' ').slice(1).join(' ')}</span>
                                </button>
                              );
                            })}
                          </div>

                          {/* Custom Amount */}
                          {showCustomTip && (
                            <div className="mb-4">
                              <input
                                type="number"
                                value={customTipAmount}
                                onChange={(e) => setCustomTipAmount(e.target.value)}
                                placeholder={selectedProvider === 'razorpay' ? '₹ Amount' : '$ Amount'}
                                className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                              />
                              <button
                                onClick={() => handleTip('custom')}
                                disabled={processingTip}
                                className="w-full mt-2 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-sm font-medium"
                              >
                                Send Custom Tip
                              </button>
                            </div>
                          )}

                          <button
                            onClick={() => setShowCustomTip(!showCustomTip)}
                            className="text-xs text-purple-400 hover:text-purple-300"
                          >
                            {showCustomTip ? 'Hide custom amount' : 'Enter custom amount'}
                          </button>

                          {/* Platform Fee Notice */}
                          <p className="text-xs text-gray-500 mt-3 text-center">
                            {platformFee}% platform fee • Creator receives {100 - platformFee}%
                          </p>

                          {processingTip && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-xl">
                              <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">About</h4>
                    <p className="text-gray-300 text-sm leading-relaxed">{comic.description}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-900/50 p-3 rounded-lg border border-white/5">
                      <p className="text-xs text-gray-500 mb-1">Author</p>
                      <div className="flex items-center space-x-2">
                        <User className="w-3 h-3 text-purple-400" />
                        <span className="text-sm font-medium text-gray-200 truncate">{comic.author_name}</span>
                      </div>
                    </div>
                    <div className="bg-gray-900/50 p-3 rounded-lg border border-white/5">
                      <p className="text-xs text-gray-500 mb-1">Published</p>
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-3 h-3 text-purple-400" />
                        <span className="text-sm font-medium text-gray-200">{formatDate(comic.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  {comic.tags.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Tags</h4>
                      <div className="flex flex-wrap gap-2">
                        {comic.tags.map((tag, i) => (
                          <span key={i} className="px-2.5 py-1 rounded-md bg-gray-800 text-xs text-gray-300 border border-gray-700 hover:border-gray-500 transition-colors cursor-default">
                            #{tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* More from Creator */}
                  {creatorComics.length > 0 && (
                    <div className="pt-6 border-t border-white/5">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">More from {comic.author_name}</h4>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {creatorComics.map((otherComic) => (
                          <Link key={otherComic.id} to={`/streamlined-comic/${otherComic.id}`} className="group block bg-gray-900/50 rounded-lg overflow-hidden border border-white/5 hover:border-purple-500/50 transition-all">
                            <div className="aspect-[2/3] relative overflow-hidden">
                              {otherComic.episodes?.[0]?.slides?.[0] ? (
                                <img
                                  src={otherComic.episodes[0].slides[0]}
                                  alt={otherComic.title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-purple-900/50 to-slate-900 flex items-center justify-center">
                                  <span className="text-purple-500/30">No Cover</span>
                                </div>
                              )}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60" />
                              <div className="absolute bottom-0 left-0 right-0 p-2">
                                <h5 className="text-white text-xs font-medium truncate">{otherComic.title}</h5>
                                <p className="text-[10px] text-gray-400">{new Date(otherComic.created_at).getFullYear()}</p>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComicViewStreamlined;
